import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Send, Mail, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '../lib/utils';

const SendSignatureModal = ({ open, onOpenChange, quoteId, clientEmail, token, onRequestSent }) => {
  const [email, setEmail] = useState(clientEmail || '');
  const [submitting, setSubmitting] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setEmail(clientEmail || '');
      setSignatureUrl(null);
    }
  }, [open, clientEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/quotes/${quoteId}/request-signature`,
        { recipient_email: email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Vérifier que la réponse est un succès avant d'afficher le toast
      if (response.status === 200) {
        toast.success('Demande de signature envoyée !');
        onRequestSent?.();
        setTimeout(() => onOpenChange(false), 1500);
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (error) {
      console.error('Request signature error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSubmitting(false);
  };

  const handleCopyLink = () => {
    if (signatureUrl) {
      navigator.clipboard.writeText(signatureUrl);
      toast.success('Lien copié !');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Envoyer pour signature
          </DialogTitle>
          <DialogDescription>
            Le client recevra un email avec un lien sécurisé pour signer ce devis.
          </DialogDescription>
        </DialogHeader>

        {!signatureUrl ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Email du destinataire</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-muted/50"
                data-testid="signature-email-input"
              />
            </div>

            <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground">
              <p className="mb-2">Le client pourra :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Voir le devis complet avec les photos</li>
                <li>Signer directement depuis son téléphone</li>
                <li>Vous serez notifié dès la signature</li>
              </ul>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={submitting || !email}
                className="bg-primary"
                data-testid="send-signature-request-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer la demande
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-500 font-medium mb-2">Email envoyé !</p>
              <p className="text-sm text-muted-foreground">
                Un email a été envoyé à <span className="text-foreground">{email}</span>.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Lien de signature (valide 7 jours)</Label>
              <div className="flex gap-2">
                <Input
                  value={signatureUrl}
                  readOnly
                  className="bg-muted/50 text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  Copier
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Vous pouvez aussi partager ce lien par SMS ou autre messagerie.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full"
              >
                Fermer
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendSignatureModal;
